import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';

// Estilos
import styles from './turmaformpage.module.css';

// API
import { useEmpresaService } from '../../services/empresaServices';
import { useTurmaService } from '../../services/turmaServices';
import { useTesteService } from '../../services/testeServices';
import { useTrilhaService } from '../../services/trilhaServices';

function TurmaFormPage() {
  const { logout } = useAuth();
  const { showToast } = useToast();
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const { idEmpresa, idTurma } = useParams();
  const { pathname } = useLocation();

  const { obterEmpresaPorId } = useEmpresaService();
  const {
    obterTurmaPorId,
    criarTurma,
    atualizarTurma,
    listarTodosLembretesTurma,
  } = useTurmaService();
  const { listarTestes } = useTesteService();
  const { listarTrilhas } = useTrilhaService();

  const isEditing = !!idTurma && pathname.includes('atualizar');

  const [empresa, setEmpresa] = useState(null);
  const [testes, setTestes] = useState([]);
  const [trilhas, setTrilhas] = useState([]);

  // Observação: para campos booleanos em <select>, usamos '' | true | false
  const [formData, setFormData] = useState({
    nome: '',
    tipoVinculo: 'teste', // 'teste' | 'trilha'
    idTeste: '',
    idTrilha: '',

    // obrigatórios
    status: '',
    dataLiberacao: '',
    dataLimite: '',

    // opcionais (somente para vínculo em teste)
    devolutiva: '',
    mensagemRetorno: '',

    // opcionais (somente para vínculo em teste)
    acompanhamento: '',
    tipoLembrete: '',
    intervaloLembreteDias: '',
    datasLembrete: [],
  });

  const [newReminderDate, setNewReminderDate] = useState('');

  // helper para exibir dd/mm/yyyy
  const formatDateBr = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const [yyyy, mm, dd] = parts;
    return `${dd}/${mm}/${yyyy}`;
  };

  useEffect(() => {
    const carregarDados = async () => {
      showLoader('Carregando dados');

      // 1) Carrega empresa
      const empresaResponse = await obterEmpresaPorId(idEmpresa);
      if (empresaResponse.success) {
        setEmpresa(empresaResponse.data);
      } else {
        alert('Erro ao carregar dados da empresa: ' + empresaResponse.message);
        if (empresaResponse.message?.toLowerCase().includes('token')) logout();
        hideLoader();
        return;
      }

      // 2) Se edição, carrega turma + lembretes
      if (isEditing) {
        const [turmaResponse, lembretesResponse] = await Promise.all([
          obterTurmaPorId(idTurma),
          listarTodosLembretesTurma(idTurma),
        ]);

        if (turmaResponse.success) {
          const turma = turmaResponse.data;

          const datasLembrete =
            lembretesResponse?.success && Array.isArray(lembretesResponse.data)
              ? lembretesResponse.data
                  .map((item) => item.dataLembrete)
                  .filter(Boolean)
              : [];

          const acompanhamentoRaw =
            typeof turma.acompanhamento !== 'undefined'
              ? Boolean(Number(turma.acompanhamento))
              : datasLembrete.length > 0;

          const tipoLembreteRaw =
            turma.tipo_lembrete || (datasLembrete.length > 0 ? 'datas' : '');

          const temIdTrilha =
            typeof turma.idTrilha !== 'undefined' && turma.idTrilha !== null;

          const tipoVinculo = temIdTrilha ? 'trilha' : 'teste';

          setFormData((prev) => ({
            ...prev,
            nome: turma.nome || '',
            tipoVinculo,

            idTeste: !temIdTrilha && turma.idTeste ? turma.idTeste.toString() : '',
            idTrilha: temIdTrilha && turma.idTrilha ? turma.idTrilha.toString() : '',

            status: typeof turma.status !== 'undefined' ? Boolean(Number(turma.status)) : '',
            dataLiberacao: turma.data_liberacao || '',
            dataLimite: turma.data_limite || '',

            // opcionais
            mensagemRetorno: turma.mensagem_retorno || '',
            devolutiva:
              typeof turma.devolutiva !== 'undefined'
                ? Boolean(Number(turma.devolutiva))
                : '',

            acompanhamento: typeof acompanhamentoRaw !== 'undefined' ? acompanhamentoRaw : '',
            tipoLembrete: tipoLembreteRaw || '',
            intervaloLembreteDias: turma.intervalo_lembrete_dias
              ? String(turma.intervalo_lembrete_dias)
              : '',
            datasLembrete,
          }));
        } else {
          showToast('Erro ao carregar turma.', 3000, 'error');
          if (turmaResponse.message?.toLowerCase().includes('token')) logout();
        }
      }

      // 3) Carrega testes e trilhas em paralelo
      const [testesResponse, trilhasResponse] = await Promise.all([
        listarTestes(),
        listarTrilhas(),
      ]);

      if (testesResponse.success) {
        setTestes(testesResponse.data || []);
      } else {
        showToast('Erro ao carregar testes.', 3000, 'error');
        if (testesResponse.message?.toLowerCase().includes('token')) logout();
      }

      if (trilhasResponse.success) {
        setTrilhas(trilhasResponse.data || []);
      } else {
        showToast('Erro ao carregar trilhas.', 3000, 'error');
        if (trilhasResponse.message?.toLowerCase().includes('token')) logout();
      }

      hideLoader();
    };

    if (idEmpresa) carregarDados();
  }, [idEmpresa, idTurma, isEditing]); // mantém igual ao seu padrão

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Selects booleanos com possibilidade de "não selecionado" => '' | true | false
    if (name === 'status' || name === 'devolutiva' || name === 'acompanhamento') {
      if (value === '') {
        setFormData((prev) => ({ ...prev, [name]: '' }));
        return;
      }
      const newValue = value === 'true';
      setFormData((prev) => ({ ...prev, [name]: newValue }));
      return;
    }

    // Troca entre teste/trilha
    if (name === 'tipoVinculo') {
      const newValue = value; // 'teste' ou 'trilha'

      setFormData((prev) => ({
        ...prev,
        tipoVinculo: newValue,

        // IDs
        idTeste: newValue === 'teste' ? prev.idTeste : '',
        idTrilha: newValue === 'trilha' ? prev.idTrilha : '',

        // Regras pedidas: se for TRILHA, manter apenas obrigatórios até Data Limite
        ...(newValue === 'trilha'
          ? {
              devolutiva: '',
              mensagemRetorno: '',
              acompanhamento: '',
              tipoLembrete: '',
              intervaloLembreteDias: '',
              datasLembrete: [],
            }
          : {}),
      }));

      // limpa input auxiliar de data
      if (newValue === 'trilha') setNewReminderDate('');
      return;
    }

    const newValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  // Adicionar uma data ao array datasLembrete
  const handleAddReminderDate = () => {
    const date = newReminderDate;
    if (!date) return;

    const { dataLiberacao, dataLimite } = formData;

    if (dataLiberacao && date < dataLiberacao) {
      showToast(
        'A data de lembrete não pode ser anterior à data de liberação.',
        3000,
        'warning'
      );
      return;
    }
    if (dataLimite && date > dataLimite) {
      showToast(
        'A data de lembrete não pode ser posterior à data limite.',
        3000,
        'warning'
      );
      return;
    }

    setFormData((prev) => {
      if (prev.datasLembrete.includes(date)) {
        showToast('Essa data já foi adicionada.', 2500, 'info');
        return prev;
      }
      const novas = [...prev.datasLembrete, date].sort();
      return { ...prev, datasLembrete: novas };
    });

    setNewReminderDate('');
  };

  const handleRemoveReminderDate = (date) => {
    setFormData((prev) => ({
      ...prev,
      datasLembrete: prev.datasLembrete.filter((d) => d !== date),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    showLoader(isEditing ? 'Atualizando turma' : 'Criando turma');

    // ===== validações realmente obrigatórias =====
    if (!formData.nome?.trim()) {
      showToast('Informe o nome da turma.', 3000, 'warning');
      hideLoader();
      return;
    }

    // status é obrigatório (endpoint exige)
    if (formData.status === '') {
      showToast('Selecione se a turma começará ativa.', 3000, 'warning');
      hideLoader();
      return;
    }

    if (!formData.dataLiberacao) {
      showToast('Informe a data inicial (liberação).', 3000, 'warning');
      hideLoader();
      return;
    }

    if (!formData.dataLimite) {
      showToast('Informe a data limite.', 3000, 'warning');
      hideLoader();
      return;
    }

    if (formData.dataLiberacao && formData.dataLimite && formData.dataLiberacao > formData.dataLimite) {
      showToast('A data inicial não pode ser maior que a data limite.', 3000, 'warning');
      hideLoader();
      return;
    }

    // Vínculo obrigatório: teste OU trilha
    if (formData.tipoVinculo === 'teste') {
      if (!formData.idTeste) {
        showToast('Selecione um teste para a turma.', 3000, 'warning');
        hideLoader();
        return;
      }
    } else {
      if (!formData.idTrilha) {
        showToast('Selecione uma trilha para a turma.', 3000, 'warning');
        hideLoader();
        return;
      }
    }

    const isTeste = formData.tipoVinculo === 'teste';
    const acompanhamentoAtivo = isTeste && formData.acompanhamento === true;

    // Se o usuário optou por acompanhamento, aí sim validamos o mínimo para funcionar (sem “exigir” sempre)
    if (acompanhamentoAtivo) {
      if (!formData.tipoLembrete) {
        showToast('Selecione o tipo de periodicidade dos lembretes.', 3000, 'warning');
        hideLoader();
        return;
      }

      if (formData.tipoLembrete === 'intervalo') {
        const dias = Number(formData.intervaloLembreteDias);
        if (!Number.isFinite(dias) || dias <= 0) {
          showToast('Informe um intervalo válido (em dias) para os lembretes.', 3000, 'warning');
          hideLoader();
          return;
        }
      }

      if (formData.tipoLembrete === 'datas') {
        if (!formData.datasLembrete || formData.datasLembrete.length === 0) {
          showToast('Adicione pelo menos uma data de lembrete.', 3000, 'warning');
          hideLoader();
          return;
        }
      }
    }

    // ===== payload =====
    const turmaJson = {
      idEmpresa: idEmpresa,
      nome: formData.nome.trim(),
      status: Boolean(formData.status),
      dataLiberacao: formData.dataLiberacao,
      dataLimite: formData.dataLimite,
    };

    if (isTeste) {
      turmaJson.idTeste = formData.idTeste;

      // Campos opcionais: só manda se o usuário escolheu/preencheu
      if (formData.devolutiva !== '') {
        turmaJson.devolutiva = Boolean(formData.devolutiva);
      }

      const msg = (formData.mensagemRetorno || '').trim();
      if (msg) {
        turmaJson.mensagemRetorno = msg;
      }

      if (formData.acompanhamento !== '') {
        turmaJson.acompanhamento = Boolean(formData.acompanhamento);
      }

      if (acompanhamentoAtivo) {
        turmaJson.acompanhamento = true;
        turmaJson.tipoLembrete = formData.tipoLembrete || null;

        if (formData.tipoLembrete === 'intervalo') {
          turmaJson.intervaloLembreteDias = Number(formData.intervaloLembreteDias);
        }

        if (formData.tipoLembrete === 'datas') {
          turmaJson.datasLembrete = formData.datasLembrete || [];
        }
      }
    } else {
      turmaJson.idTrilha = formData.idTrilha;
      // Regra pedida: para TRILHA, não envia devolutiva/mensagem/acompanhamento/lembretes.
    }

    if (isEditing) {
      turmaJson.id = idTurma;
    }

    const response = isEditing
      ? await atualizarTurma(turmaJson)
      : await criarTurma(turmaJson);

    if (response.success) {
      showToast(
        isEditing ? 'Turma atualizada com sucesso!' : 'Turma criada com sucesso!',
        3000,
        'success'
      );
      navigate(`/empresas/${idEmpresa}`);
    } else {
      showToast(
        isEditing ? 'Erro ao atualizar turma.' : 'Erro ao criar turma.',
        3000,
        'error'
      );
      console.log(response.message);
      if (response.message?.toLowerCase().includes('token')) logout();
    }

    hideLoader();
  };

  const breadcrumbIds = [];
  if (empresa) breadcrumbIds.push({ id: idEmpresa, label: empresa.nome, place: 1 });
  if (isEditing && formData.nome)
    breadcrumbIds.push({ id: idTurma, label: formData.nome, place: 2 });

  const isTeste = formData.tipoVinculo === 'teste';
  const acompanhamentoAtivo = isTeste && formData.acompanhamento === true;

  return (
    <div className={styles.TurmaFormPage}>
      <div className={styles.containerTop}>
        <Breadcrumbs idLabels={breadcrumbIds} />

        <h1 className={styles.title}>
          {idTurma ? 'Editar Turma' : 'Cadastro de Turma'}
        </h1>

        {idTurma ? (
          <FriendlyPhrase page="atualizarTurma" />
        ) : (
          <FriendlyPhrase page="adicionarTurma" />
        )}
      </div>

      <form onSubmit={handleSubmit} className={styles.formulario}>
        <label>
          Nome:
          <input
            type="text"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            placeholder="Ex.: Turma de Teste de Personalidade"
            required
          />
        </label>

        {/* Controle: a turma será vinculada a um teste ou a uma trilha? */}
        <label>
          Tipo de vínculo da turma:
          <select
            name="tipoVinculo"
            value={formData.tipoVinculo}
            onChange={handleChange}
            className={styles.selectInput}
            required
          >
            <option value="teste">Teste</option>
            <option value="trilha">Trilha</option>
          </select>
        </label>

        {/* Se for turma de TESTE, mostra o select de testes */}
        {formData.tipoVinculo === 'teste' && (
          <label>
            Teste:
            <select
              name="idTeste"
              value={formData.idTeste}
              onChange={handleChange}
              className={styles.selectInput}
              required
            >
              <option value="" disabled>
                Selecione um teste
              </option>
              {testes.map((teste) => (
                <option key={teste.id} value={teste.id}>
                  {teste.nome}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Se for turma de TRILHA, mostra o select de trilhas */}
        {formData.tipoVinculo === 'trilha' && (
          <label>
            Trilha:
            <select
              name="idTrilha"
              value={formData.idTrilha}
              onChange={handleChange}
              className={styles.selectInput}
              required
            >
              <option value="" disabled>
                Selecione uma trilha
              </option>
              {trilhas.map((trilha) => (
                <option key={trilha.id} value={trilha.id}>
                  {trilha.titulo}
                </option>
              ))}
            </select>
          </label>
        )}

        <label>
          A turma começará ativa?
          <select
            name="status"
            value={formData.status === '' ? '' : String(formData.status)}
            onChange={handleChange}
            className={styles.selectInput}
            required
          >
            <option value="" disabled>
              Selecione uma opção
            </option>
            <option value="true">Sim</option>
            <option value="false">Não</option>
          </select>
        </label>

        <label>
          Data Inicial:
          <input
            type="date"
            name="dataLiberacao"
            value={formData.dataLiberacao}
            onChange={handleChange}
            required
            className={styles.dateInput}
          />
        </label>

        <label>
          Data Limite:
          <input
            type="date"
            name="dataLimite"
            value={formData.dataLimite}
            onChange={handleChange}
            required
            className={styles.dateInput}
          />
        </label>

        {/* ======================
            A PARTIR DAQUI: apenas para vínculo em TESTE
            (para TRILHA, para em "Data Limite" como você pediu)
           ====================== */}
        {isTeste && (
          <>
            <label>
              Enviar devolutiva em PDF via WhatsApp? <span className={styles.mutedNote}>(opcional)</span>
              <select
                name="devolutiva"
                value={formData.devolutiva === '' ? '' : String(formData.devolutiva)}
                onChange={handleChange}
                className={styles.selectInput}
              >
                <option value="">Não definir</option>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </label>

            <label>
              Mensagem que o usuário irá receber na devolutiva: <span className={styles.mutedNote}>(opcional)</span>
              <input
                type="text"
                name="mensagemRetorno"
                placeholder="Ex.: Olá! Aqui está sua devolutiva. Obrigado por responder!"
                value={formData.mensagemRetorno}
                onChange={handleChange}
              />
            </label>

            {/* ===== Acompanhamento (opcional) ===== */}
            <label>
              Acompanhamento com lembretes automáticos? <span className={styles.mutedNote}>(opcional)</span>
              <select
                name="acompanhamento"
                value={formData.acompanhamento === '' ? '' : String(formData.acompanhamento)}
                onChange={handleChange}
                className={styles.selectInput}
              >
                <option value="">Não definir</option>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </label>

            {acompanhamentoAtivo && (
              <>
                <label>
                  Tipo de periodicidade dos lembretes:
                  <select
                    name="tipoLembrete"
                    value={formData.tipoLembrete}
                    onChange={handleChange}
                    className={styles.selectInput}
                  >
                    <option value="" disabled>
                      Selecione uma opção
                    </option>
                    <option value="intervalo">A cada X dias</option>
                    <option value="datas">Datas específicas (calendário)</option>
                  </select>
                </label>

                {formData.tipoLembrete === 'intervalo' && (
                  <label>
                    Enviar lembrete a cada (dias):
                    <input
                      type="number"
                      min={1}
                      name="intervaloLembreteDias"
                      value={formData.intervaloLembreteDias}
                      onChange={handleChange}
                      className={styles.dateInput}
                      placeholder="Ex.: 7"
                    />
                  </label>
                )}

                {formData.tipoLembrete === 'datas' && (
                  <div className={styles.lembretesContainer}>
                    <label>
                      Selecionar datas de lembrete:
                      <div className={styles.lembreteInputRow}>
                        <input
                          type="date"
                          value={newReminderDate}
                          onChange={(e) => setNewReminderDate(e.target.value)}
                          className={styles.dateInput}
                          min={formData.dataLiberacao || undefined}
                          max={formData.dataLimite || undefined}
                        />
                        <button
                          type="button"
                          onClick={handleAddReminderDate}
                          className={styles.addDateButton}
                        >
                          Adicionar data
                        </button>
                      </div>
                      <small className={styles.mutedNote}>
                        A data mínima é a data de liberação e a máxima é a data limite.
                      </small>
                    </label>

                    {formData.datasLembrete && formData.datasLembrete.length > 0 && (
                      <div className={styles.datePillsWrapper}>
                        <p className={styles.mutedNote}>Datas de lembrete selecionadas:</p>
                        <div className={styles.datePills}>
                          {formData.datasLembrete.map((d) => (
                            <span
                              key={d}
                              className={styles.datePill}
                              title={formatDateBr(d)}
                            >
                              {formatDateBr(d)}
                              <button
                                type="button"
                                onClick={() => handleRemoveReminderDate(d)}
                                className={styles.removeDateBtn}
                                title="Remover data"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}

        <div className={styles.submitArea}>
          <button type="submit" className={styles.submitButton}>
            {isEditing ? 'Atualizar Turma' : 'Salvar Turma'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TurmaFormPage;